import React, { useState, useEffect, useRef, useCallback } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  GetApp as DownloadIcon,
  FilterList as FilterIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import apiClient from '../../api/client';

const KnowledgeGraph = ({ documentId = null }) => {
  const fgRef = useRef();

  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNode, setSelectedNode] = useState(null);
  const [graphStats, setGraphStats] = useState(null);

  // Visualization settings
  const [settings, setSettings] = useState({
    nodeSize: 8,
    linkWidth: 2,
    linkDistance: 50,
    chargeStrength: -100,
    showLabels: true,
    colorByType: true,
    physics: true
  });

  // Node type colors
  const nodeColors = {
    Document: '#FF6B6B',
    Entity: '#4ECDC4',
    Country: '#45B7D1',
    Organization: '#96CEB4',
    Person: '#FFEAA7',
    Location: '#74B9FF',
    Event: '#A29BFE',
    default: '#95A5A6'
  };

  // Relationship type colors
  const linkColors = {
    INCREASES: '#FF6B6B',
    STRENGTHENS: '#4ECDC4',
    RELATES_TO: '#95A5A6',
    HAS_CHUNK: '#DFE6E9',
    MENTIONS: '#74B9FF',
    default: '#B2BEC3'
  };

  // Transform backend data to force-graph format
  const transformGraphData = (data) => {
    const nodes = data.nodes.map(node => ({
      id: node.id,
      name: node.label || node.id,
      type: node.type,
      properties: node.properties,
      color: nodeColors[node.type] || nodeColors.default,
      val: settings.nodeSize
    }));

    const links = data.edges.map(edge => ({
      source: edge.source,
      target: edge.target,
      type: edge.type,
      label: edge.label,
      properties: edge.properties,
      color: linkColors[edge.type] || linkColors.default,
      width: settings.linkWidth
    }));

    return { nodes, links };
  };

  // Fetch graph data
  const fetchGraphData = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = '/graph';

      if (documentId) {
        url = `/graph/document/${documentId}`;
      } else if (searchQuery) {
        url = `/graph/search?q=${encodeURIComponent(searchQuery)}`;
      }

      console.log('Fetching graph from:', url);

      // First fetch stats (smaller response)
      try {
        const statsResponse = await apiClient.get('/graph/stats');
        console.log('Stats response:', statsResponse);
        const statsData = statsResponse?.data?.data || statsResponse?.data;
        if (statsData) {
          setGraphStats(statsData);
        }
      } catch (statsErr) {
        console.error('Error fetching stats:', statsErr);
      }

      // Then fetch the graph data
      const response = await apiClient.get(url, {
        params: {
          limit: 1000 // Limit nodes for performance
        }
      });

      console.log('Graph response full:', response);
      console.log('Graph response.data:', response?.data);
      console.log('Graph response.data.data:', response?.data?.data);

      // Check if response exists
      if (!response) {
        console.error('No response received');
        setError('No response from server');
        return;
      }

      // Extract data from the response - the backend returns { data: { nodes, edges }, meta: {...} }
      let graphResponseData;

      if (response.data && response.data.data && (response.data.data.nodes || response.data.data.edges)) {
        // Standard wrapped response from our API
        graphResponseData = response.data.data;
      } else if (response.data && (response.data.nodes || response.data.edges)) {
        // Direct data in response.data
        graphResponseData = response.data;
      } else if (response.nodes || response.edges) {
        // Direct graph data
        graphResponseData = response;
      } else {
        console.error('Invalid response structure - no nodes or edges found');
        console.error('Full response:', response);
        setError('Invalid response structure');
        return;
      }

      console.log('Graph data to transform:', graphResponseData);
      const transformedData = transformGraphData(graphResponseData);
      setGraphData(transformedData);

    } catch (err) {
      console.error('Error fetching graph data:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        request: err.request
      });

      // Better error handling
      if (err.response) {
        // Server responded with error
        setError(err.response.data?.error?.message || `Server error: ${err.response.status}`);
      } else if (err.request) {
        // Request was made but no response
        setError('No response from server - please check connection');
      } else {
        // Something else happened
        setError(err.message || 'Failed to load graph data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGraphData();
  }, [documentId]);

  // Handle node click
  const handleNodeClick = useCallback(node => {
    setSelectedNode(node);
  }, []);

  // Handle node hover
  const handleNodeHover = useCallback(node => {
    // Highlight connected nodes
    if (node && fgRef.current) {
      const highlightNodes = new Set();
      const highlightLinks = new Set();

      if (node) {
        highlightNodes.add(node);
        graphData.links.forEach(link => {
          if (link.source.id === node.id) {
            highlightNodes.add(link.target);
            highlightLinks.add(link);
          }
          if (link.target.id === node.id) {
            highlightNodes.add(link.source);
            highlightLinks.add(link);
          }
        });
      }

      // Update node colors
      const newNodes = graphData.nodes.map(n => ({
        ...n,
        color: highlightNodes.has(n) ? n.color : '#E0E0E0'
      }));

      // This would require state update for highlighting
      // For now, we'll use the built-in hover effects
    }
  }, [graphData]);

  // Zoom controls
  const handleZoomIn = () => {
    if (fgRef.current) {
      fgRef.current.zoom(1.2);
    }
  };

  const handleZoomOut = () => {
    if (fgRef.current) {
      fgRef.current.zoom(0.8);
    }
  };

  const handleCenter = () => {
    if (fgRef.current) {
      fgRef.current.zoomToFit(400);
    }
  };

  // Download graph as image
  const handleDownload = () => {
    if (fgRef.current) {
      const canvas = fgRef.current.scene().renderer().domElement;
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'knowledge-graph.png';
      link.href = url;
      link.click();
    }
  };

  // Node label renderer
  const getNodeLabel = (node) => {
    if (!settings.showLabels) return '';
    return node.name;
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Controls Bar */}
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search entities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') fetchGraphData();
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <ButtonGroup size="small">
                <Tooltip title="Zoom In">
                  <Button onClick={handleZoomIn}>
                    <ZoomInIcon />
                  </Button>
                </Tooltip>
                <Tooltip title="Zoom Out">
                  <Button onClick={handleZoomOut}>
                    <ZoomOutIcon />
                  </Button>
                </Tooltip>
                <Tooltip title="Center View">
                  <Button onClick={handleCenter}>
                    <CenterIcon />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              <ButtonGroup size="small">
                <Tooltip title="Refresh">
                  <Button onClick={fetchGraphData}>
                    <RefreshIcon />
                  </Button>
                </Tooltip>
                <Tooltip title="Download Image">
                  <Button onClick={handleDownload}>
                    <DownloadIcon />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showLabels}
                    onChange={(e) => setSettings({...settings, showLabels: e.target.checked})}
                  />
                }
                label="Labels"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.physics}
                    onChange={(e) => setSettings({...settings, physics: e.target.checked})}
                  />
                }
                label="Physics"
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
        {/* Graph Canvas */}
        <Paper sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1
            }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <ForceGraph2D
              ref={fgRef}
              graphData={graphData}
              nodeLabel={getNodeLabel}
              nodeColor={node => node.color}
              nodeVal={node => node.val}
              linkColor={link => link.color}
              linkWidth={link => link.width}
              linkLabel={link => link.type}
              onNodeClick={handleNodeClick}
              onNodeHover={handleNodeHover}
              enableNodeDrag={settings.physics}
              enableZoomPanInteraction={true}
              linkDirectionalArrowLength={6}
              linkDirectionalArrowRelPos={1}
              d3AlphaDecay={settings.physics ? 0.02 : 0}
              d3VelocityDecay={0.3}
              cooldownTime={3000}
              nodeCanvasObject={(node, ctx, globalScale) => {
                // Custom node rendering
                ctx.fillStyle = node.color;
                ctx.beginPath();
                ctx.arc(node.x, node.y, node.val, 0, 2 * Math.PI, false);
                ctx.fill();

                // Draw label
                if (settings.showLabels && globalScale > 0.5) {
                  ctx.font = `${10/globalScale}px Sans-serif`;
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  ctx.fillStyle = '#333';
                  ctx.fillText(node.name, node.x, node.y + node.val + 5/globalScale);
                }
              }}
            />
          )}
        </Paper>

        {/* Side Panel */}
        <Paper sx={{ width: 320, p: 2, overflow: 'auto' }}>
          {/* Graph Stats */}
          {graphStats && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Graph Statistics
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Nodes:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {graphStats.nodeCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Edges:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {graphStats.edgeCount}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Selected Node Details */}
          {selectedNode && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Node Details
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedNode.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> <Chip label={selectedNode.type} size="small" />
                  </Typography>
                  {selectedNode.properties && Object.entries(selectedNode.properties).map(([key, value]) => (
                    <Typography key={key} variant="body2">
                      <strong>{key}:</strong> {String(value)}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Settings */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Visualization Settings
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Node Size
                </Typography>
                <Slider
                  value={settings.nodeSize}
                  onChange={(e, value) => setSettings({...settings, nodeSize: value})}
                  min={2}
                  max={20}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Link Width
                </Typography>
                <Slider
                  value={settings.linkWidth}
                  onChange={(e, value) => setSettings({...settings, linkWidth: value})}
                  min={1}
                  max={10}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Charge Strength
                </Typography>
                <Slider
                  value={settings.chargeStrength}
                  onChange={(e, value) => setSettings({...settings, chargeStrength: value})}
                  min={-500}
                  max={0}
                  valueLabelDisplay="auto"
                />
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Box>
  );
};

export default KnowledgeGraph;